import { ColDef, ColGroupDef } from "ag-grid-community";

import { ColumnType } from "components/AgGrid/constants";

import { Column } from "constants/columns";
import { Species } from "constants/species";

import formatters from "lib/agGrid/formatters";
import getters from "lib/agGrid/getters";

type ColumnDefinitions = Record<string, ColDef>;

export const advancedDraftingColumnDefs: ColumnDefinitions = {
  [Column.SaleLot.AGE_MONTHS]: {
    // only relevant for cattle
    colId: Column.SaleLot.AGE_MONTHS,
    headerName: "Age (Months)",
    valueGetter: getters.ageMonthsGetter,
    valueFormatter: getters.ageMonthsFormatter,
    type: ColumnType.NUMERIC,
    enablePivot: true,
    enableRowGroup: true,
  },
  [Column.SaleLot.DROP_START]: {
    // only relevant for sheep
    colId: Column.SaleLot.DROP_START,
    field: "draftingAttributes.dropStart",
    headerName: "Drop Start",
    valueFormatter: formatters.monthYearFormatter,
    type: ColumnType.DATE,
  },
  [Column.SaleLot.DROP_END]: {
    // only relevant for sheep
    colId: Column.SaleLot.DROP_END,
    field: "draftingAttributes.dropEnd",
    headerName: "Drop End",
    valueFormatter: formatters.monthYearFormatter,
    type: ColumnType.DATE,
  },
  [Column.SaleLot.BLOODLINE]: {
    colId: Column.SaleLot.BLOODLINE,
    field: "draftingAttributes.bloodline",
    headerName: "Bloodline",
    type: ColumnType.STRING,
    enablePivot: true,
    enableRowGroup: true,
  },
  [Column.SaleLot.X_BLOODLINE]: {
    colId: Column.SaleLot.X_BLOODLINE,
    field: "draftingAttributes.xBloodline",
    headerName: "X-Bloodline",
    type: ColumnType.STRING,
    enablePivot: true,
    enableRowGroup: true,
  },
  [Column.SaleLot.VENDOR_BRED]: {
    colId: Column.SaleLot.VENDOR_BRED,
    headerName: "Vendor Bred",
    valueGetter: getters.vendorBredGetter,
    type: ColumnType.BOOLEAN,
  },
  // Fat Score columns (cattle 1-6, sheep 1-5)
  [Column.SaleLot.FAT_SCORE_1]: {
    colId: Column.SaleLot.FAT_SCORE_1,
    field: "draftingAttributes.fatScore1",
    headerName: "Score 1",
    type: ColumnType.NUMERIC,
  },
  [Column.SaleLot.FAT_SCORE_2]: {
    colId: Column.SaleLot.FAT_SCORE_2,
    field: "draftingAttributes.fatScore2",
    headerName: "Score 2",
    type: ColumnType.NUMERIC,
  },
  [Column.SaleLot.FAT_SCORE_3]: {
    colId: Column.SaleLot.FAT_SCORE_3,
    field: "draftingAttributes.fatScore3",
    headerName: "Score 3",
    type: ColumnType.NUMERIC,
  },
  [Column.SaleLot.FAT_SCORE_4]: {
    colId: Column.SaleLot.FAT_SCORE_4,
    field: "draftingAttributes.fatScore4",
    headerName: "Score 4",
    type: ColumnType.NUMERIC,
  },
  [Column.SaleLot.FAT_SCORE_5]: {
    colId: Column.SaleLot.FAT_SCORE_5,
    field: "draftingAttributes.fatScore5",
    headerName: "Score 5",
    type: ColumnType.NUMERIC,
  },
  [Column.SaleLot.FAT_SCORE_6]: {
    colId: Column.SaleLot.FAT_SCORE_6,
    field: "draftingAttributes.fatScore6",
    headerName: "Score 6",
    type: ColumnType.NUMERIC,
  },
  // Muscle Score columns (cattle only)
  [Column.SaleLot.MUSCLE_SCORE_1]: {
    colId: Column.SaleLot.MUSCLE_SCORE_1,
    field: "draftingAttributes.muscleScore1",
    headerName: "Score 1",
    type: ColumnType.NUMERIC,
  },
  [Column.SaleLot.MUSCLE_SCORE_2]: {
    colId: Column.SaleLot.MUSCLE_SCORE_2,
    field: "draftingAttributes.muscleScore2",
    headerName: "Score 2",
    type: ColumnType.NUMERIC,
  },
  [Column.SaleLot.MUSCLE_SCORE_3]: {
    colId: Column.SaleLot.MUSCLE_SCORE_3,
    field: "draftingAttributes.muscleScore3",
    headerName: "Score 3",
    type: ColumnType.NUMERIC,
  },
  [Column.SaleLot.MUSCLE_SCORE_4]: {
    colId: Column.SaleLot.MUSCLE_SCORE_4,
    field: "draftingAttributes.muscleScore4",
    headerName: "Score 4",
    type: ColumnType.NUMERIC,
  },
  [Column.SaleLot.MUSCLE_SCORE_5]: {
    colId: Column.SaleLot.MUSCLE_SCORE_5,
    field: "draftingAttributes.muscleScore5",
    headerName: "Score 5",
    type: ColumnType.NUMERIC,
  },
  // Horn columns (cattle only)
  [Column.SaleLot.HORN_POLLED]: {
    colId: Column.SaleLot.HORN_POLLED,
    field: "draftingAttributes.hornPolled",
    headerName: "Polled",
    type: ColumnType.NUMERIC,
  },
  [Column.SaleLot.HORN_HORNED]: {
    colId: Column.SaleLot.HORN_HORNED,
    field: "draftingAttributes.hornHorned",
    headerName: "Horned",
    type: ColumnType.NUMERIC,
  },
  [Column.SaleLot.HORN_DEHORNED]: {
    colId: Column.SaleLot.HORN_DEHORNED,
    field: "draftingAttributes.hornDehorned",
    headerName: "Dehorned",
    type: ColumnType.NUMERIC,
  },
  [Column.SaleLot.HORN_TIPPED]: {
    colId: Column.SaleLot.HORN_TIPPED,
    field: "draftingAttributes.hornTipped",
    headerName: "Tipped",
    type: ColumnType.NUMERIC,
  },
  [Column.SaleLot.HORN_SCURRED]: {
    colId: Column.SaleLot.HORN_SCURRED,
    field: "draftingAttributes.hornScurred",
    headerName: "Scurred",
    type: ColumnType.NUMERIC,
  },
  // Condition columns (sheep only)
  [Column.SaleLot.CONDITION_STORE]: {
    colId: Column.SaleLot.CONDITION_STORE,
    field: "draftingAttributes.conditionStore",
    headerName: "Store",
    type: ColumnType.BOOLEAN,
  },
  [Column.SaleLot.CONDITION_FORWARD]: {
    colId: Column.SaleLot.CONDITION_FORWARD,
    field: "draftingAttributes.conditionForward",
    headerName: "Forward",
    type: ColumnType.BOOLEAN,
  },
  [Column.SaleLot.CONDITION_PRIME]: {
    colId: Column.SaleLot.CONDITION_PRIME,
    field: "draftingAttributes.conditionPrime",
    headerName: "Prime",
    type: ColumnType.BOOLEAN,
  },
  // Weight columns
  [Column.SaleLot.EST_CARCASS_WEIGHT]: {
    colId: Column.SaleLot.EST_CARCASS_WEIGHT,
    field: "draftingAttributes.estCarcassWeight",
    headerName: "Est Carcass Weight",
    type: ColumnType.WEIGHT_FROM_GRAMS,
  },
  [Column.SaleLot.DRESSING_PERCENT]: {
    colId: Column.SaleLot.DRESSING_PERCENT,
    field: "draftingAttributes.dressingPercent",
    headerName: "Dressing %",
    valueFormatter: formatters.percentageFormatter,
    type: ColumnType.NUMERIC,
  },
  [Column.SaleLot.MIN_MASS_GRAMS]: {
    // sheep only
    colId: Column.SaleLot.MIN_MASS_GRAMS,
    field: "draftingAttributes.minMassGrams",
    headerName: "Min Weight",
    type: ColumnType.WEIGHT_FROM_GRAMS,
  },
  [Column.SaleLot.MAX_MASS_GRAMS]: {
    // sheep only
    colId: Column.SaleLot.MAX_MASS_GRAMS,
    field: "draftingAttributes.maxMassGrams",
    headerName: "Max Weight",
    type: ColumnType.WEIGHT_FROM_GRAMS,
  },
  [Column.SaleLot.IS_ESTIMATED_WEIGHT]: {
    // sheep only
    colId: Column.SaleLot.IS_ESTIMATED_WEIGHT,
    field: "draftingAttributes.isEstimatedWeight",
    headerName: "Weight Type",
    valueGetter: getters.estimatedWeightGetter,
    type: ColumnType.STRING,
    enablePivot: true,
    enableRowGroup: true,
  },
  [Column.SaleLot.WEIGH_LOCATION]: {
    // sheep only
    colId: Column.SaleLot.WEIGH_LOCATION,
    field: "draftingAttributes.weighLocation",
    headerName: "Weigh Location",
    type: ColumnType.STRING,
    enablePivot: true,
    enableRowGroup: true,
  },
  // Preg Status columns (sheep only)
  [Column.SaleLot.PREG_STATUS]: {
    colId: Column.SaleLot.PREG_STATUS,
    field: "draftingAttributes.pregStatus",
    headerName: "Preg Status",
    type: ColumnType.STRING,
    enablePivot: true,
    enableRowGroup: true,
  },
  [Column.SaleLot.SIL]: {
    colId: Column.SaleLot.SIL,
    field: "draftingAttributes.sil",
    headerName: "SIL %",
    valueGetter: getters.floatGetter,
    valueFormatter: formatters.percentageFormatter,
    type: ColumnType.NUMERIC,
  },
  [Column.SaleLot.JOINED_START]: {
    colId: Column.SaleLot.JOINED_START,
    field: "draftingAttributes.joinedStart",
    headerName: "Joined Start",
    valueFormatter: formatters.monthYearFormatter,
    type: ColumnType.DATE,
  },
  [Column.SaleLot.JOINED_END]: {
    colId: Column.SaleLot.JOINED_END,
    field: "draftingAttributes.joinedEnd",
    headerName: "Joined End",
    valueFormatter: formatters.monthYearFormatter,
    type: ColumnType.DATE,
  },
  [Column.SaleLot.DEPASTURED]: {
    colId: Column.SaleLot.DEPASTURED,
    field: "draftingAttributes.depasturedId",
    headerName: "Depastured",
    valueGetter: getters.breedNameGetter,
    type: ColumnType.STRING,
    enablePivot: true,
    enableRowGroup: true,
  },
  // Shearing columns (sheep only)
  [Column.SaleLot.SHEARING]: {
    colId: Column.SaleLot.SHEARING,
    field: "draftingAttributes.shearing",
    headerName: "Shearing Date",
    valueFormatter: formatters.monthYearFormatter,
    type: ColumnType.DATE,
  },
  [Column.SaleLot.SHEARING_STATUS]: {
    colId: Column.SaleLot.SHEARING_STATUS,
    field: "draftingAttributes.shearingStatus",
    headerName: "Shearing Status",
    type: ColumnType.STRING,
    enablePivot: true,
    enableRowGroup: true,
  },
  // Frame Structure (both species)
  [Column.SaleLot.FRAME_STRUCTURE]: {
    colId: Column.SaleLot.FRAME_STRUCTURE,
    field: "draftingAttributes.frameStructure",
    headerName: "Frame Structure",
    type: ColumnType.STRING,
    enablePivot: true,
    enableRowGroup: true,
  },
  // Advanced Breeds (cattle only)
  [Column.SaleLot.ADVANCED_BREED_1_ID]: {
    colId: Column.SaleLot.ADVANCED_BREED_1_ID,
    field: "draftingAttributes.advancedBreed1Id",
    headerName: "Breed 1",
    valueGetter: getters.breedNameGetter,
    type: ColumnType.STRING,
    enablePivot: true,
    enableRowGroup: true,
  },
  [Column.SaleLot.ADVANCED_BREED_1_HD]: {
    colId: Column.SaleLot.ADVANCED_BREED_1_HD,
    field: "draftingAttributes.advancedBreed1Hd",
    headerName: "Breed 1 Hd",
    type: ColumnType.NUMERIC,
  },
  [Column.SaleLot.ADVANCED_BREED_2_ID]: {
    colId: Column.SaleLot.ADVANCED_BREED_2_ID,
    field: "draftingAttributes.advancedBreed2Id",
    headerName: "Breed 2",
    valueGetter: getters.breedNameGetter,
    type: ColumnType.STRING,
    enablePivot: true,
    enableRowGroup: true,
  },
  [Column.SaleLot.ADVANCED_BREED_2_HD]: {
    colId: Column.SaleLot.ADVANCED_BREED_2_HD,
    field: "draftingAttributes.advancedBreed2Hd",
    headerName: "Breed 2 Hd",
    type: ColumnType.NUMERIC,
  },
  [Column.SaleLot.ADVANCED_BREED_3_ID]: {
    colId: Column.SaleLot.ADVANCED_BREED_3_ID,
    field: "draftingAttributes.advancedBreed3Id",
    headerName: "Breed 3",
    valueGetter: getters.breedNameGetter,
    type: ColumnType.STRING,
    enablePivot: true,
    enableRowGroup: true,
  },
  [Column.SaleLot.ADVANCED_BREED_3_HD]: {
    colId: Column.SaleLot.ADVANCED_BREED_3_HD,
    field: "draftingAttributes.advancedBreed3Hd",
    headerName: "Breed 3 Hd",
    type: ColumnType.NUMERIC,
  },
  // Other fields
  [Column.SaleLot.PUBLIC_FAULT_DESCRIPTION]: {
    colId: Column.SaleLot.PUBLIC_FAULT_DESCRIPTION,
    field: "draftingAttributes.publicFaultDescription",
    headerName: "Fault Description",
    type: ColumnType.STRING,
  },
  [Column.SaleLot.PART_OF_RUN]: {
    colId: Column.SaleLot.PART_OF_RUN,
    field: "draftingAttributes.partOfRun",
    headerName: "Part of Run",
    type: ColumnType.STRING,
  },
  [Column.SaleLot.BY_SIRE]: {
    // sheep only
    colId: Column.SaleLot.BY_SIRE,
    field: "draftingAttributes.bySire",
    headerName: "By Sire",
    type: ColumnType.STRING,
    enablePivot: true,
    enableRowGroup: true,
  },
  [Column.SaleLot.EST_LIVE_WEIGHT]: {
    colId: Column.SaleLot.EST_LIVE_WEIGHT,
    field: "estimatedAverageMassGrams",
    headerName: "Est Live Weight",
    type: ColumnType.WEIGHT_FROM_GRAMS,
  },
};

export const speciesAccreditationChildColumns = (
  speciesId: number,
  ColumnDefs: ColumnDefinitions,
): ColDef[] => {
  if (speciesId === Species.CATTLE) {
    return Object.values(
      Column.DraftingAttributes.CattleDraftingAttributes,
    ).map(colId => ColumnDefs[colId]);
  } else if (speciesId === Species.SHEEP) {
    return Object.values(Column.DraftingAttributes.SheepDraftingAttributes).map(
      colId => ColumnDefs[colId],
    );
  }
  // returning an empty children list - hides the parent :okay_hand
  return [];
};

export const dentitionColumns = (
  speciesId: number,
  enableDentition: boolean,
  ColumnDefs: ColumnDefinitions,
): ColGroupDef => ({
  headerName: "Dentition",
  children: [
    ColumnDefs[Column.SaleLot.IS_NOT_MOUTHED],
    ColumnDefs[
      speciesId === Species.CATTLE
        ? Column.SaleLot.CATTLE_DENTITION_0
        : Column.SaleLot.SHEEP_DENTITION_0
    ],
    ColumnDefs[Column.SaleLot.DENTITION_2],
    ColumnDefs[Column.SaleLot.DENTITION_4],
    ColumnDefs[Column.SaleLot.DENTITION_6],
    ColumnDefs[Column.SaleLot.DENTITION_8],
    ColumnDefs[Column.SaleLot.DENTITION_BROKEN],
    enableDentition && ColumnDefs[Column.SaleLot.EST_DENTITION],
  ].filter(Boolean) as ColDef[],
});

export const advancedDraftingColumns = (
  speciesId: number,
  ColumnDefs: ColumnDefinitions,
): (ColDef | ColGroupDef)[] =>
  [
    // Species-conditional identity columns
    speciesId === Species.CATTLE && ColumnDefs[Column.SaleLot.IS_WEANED],
    ColumnDefs[Column.SaleLot.VENDOR_BRED],
    speciesId === Species.CATTLE && ColumnDefs[Column.SaleLot.AGE_MONTHS],
    speciesId === Species.SHEEP && {
      headerName: "Drop",
      children: [
        ColumnDefs[Column.SaleLot.DROP_START],
        ColumnDefs[Column.SaleLot.DROP_END],
      ],
    },
    speciesId === Species.CATTLE
      ? ColumnDefs[Column.SaleLot.BLOODLINE]
      : speciesId === Species.SHEEP
        ? ColumnDefs[Column.SaleLot.X_BLOODLINE]
        : null,
    speciesId === Species.SHEEP && ColumnDefs[Column.SaleLot.BY_SIRE],
    // Fat Score columns (cattle 1-6, sheep 1-5)
    {
      headerName: "Fat Score",
      children: [
        ColumnDefs[Column.SaleLot.FAT_SCORE_1],
        ColumnDefs[Column.SaleLot.FAT_SCORE_2],
        ColumnDefs[Column.SaleLot.FAT_SCORE_3],
        ColumnDefs[Column.SaleLot.FAT_SCORE_4],
        ColumnDefs[Column.SaleLot.FAT_SCORE_5],
        speciesId !== Species.SHEEP && ColumnDefs[Column.SaleLot.FAT_SCORE_6],
      ].filter(Boolean) as ColDef[],
    },
    speciesId === Species.CATTLE && {
      headerName: "Muscle Score",
      children: [
        ColumnDefs[Column.SaleLot.MUSCLE_SCORE_1],
        ColumnDefs[Column.SaleLot.MUSCLE_SCORE_2],
        ColumnDefs[Column.SaleLot.MUSCLE_SCORE_3],
        ColumnDefs[Column.SaleLot.MUSCLE_SCORE_4],
        ColumnDefs[Column.SaleLot.MUSCLE_SCORE_5],
      ],
    },
    speciesId === Species.CATTLE && {
      headerName: "Horn",
      children: [
        ColumnDefs[Column.SaleLot.HORN_POLLED],
        ColumnDefs[Column.SaleLot.HORN_HORNED],
        ColumnDefs[Column.SaleLot.HORN_DEHORNED],
        ColumnDefs[Column.SaleLot.HORN_TIPPED],
        ColumnDefs[Column.SaleLot.HORN_SCURRED],
      ],
    },
    speciesId === Species.SHEEP && {
      headerName: "Condition",
      children: [
        ColumnDefs[Column.SaleLot.CONDITION_STORE],
        ColumnDefs[Column.SaleLot.CONDITION_FORWARD],
        ColumnDefs[Column.SaleLot.CONDITION_PRIME],
      ],
    },
    {
      headerName: "Drafting Weight",
      children: [
        ColumnDefs[Column.SaleLot.EST_LIVE_WEIGHT],
        ColumnDefs[Column.SaleLot.EST_CARCASS_WEIGHT],
        ColumnDefs[Column.SaleLot.DRESSING_PERCENT],
        speciesId === Species.SHEEP &&
          ColumnDefs[Column.SaleLot.MIN_MASS_GRAMS],
        speciesId === Species.SHEEP &&
          ColumnDefs[Column.SaleLot.MAX_MASS_GRAMS],
        speciesId === Species.SHEEP &&
          ColumnDefs[Column.SaleLot.IS_ESTIMATED_WEIGHT],
        speciesId === Species.SHEEP &&
          ColumnDefs[Column.SaleLot.WEIGH_LOCATION],
      ].filter(Boolean) as ColDef[],
    },
    speciesId === Species.SHEEP && {
      headerName: "Preg Status",
      children: [
        ColumnDefs[Column.SaleLot.PREG_STATUS],
        ColumnDefs[Column.SaleLot.SIL],
        ColumnDefs[Column.SaleLot.JOINED_START],
        ColumnDefs[Column.SaleLot.JOINED_END],
        ColumnDefs[Column.SaleLot.DEPASTURED],
      ],
    },
    speciesId === Species.SHEEP && {
      headerName: "Shearing",
      children: [
        ColumnDefs[Column.SaleLot.SHEARING],
        ColumnDefs[Column.SaleLot.SHEARING_STATUS],
      ],
    },
    ColumnDefs[Column.SaleLot.FRAME_STRUCTURE],
    {
      headerName: "Advanced Breeds",
      children: [
        ColumnDefs[Column.SaleLot.ADVANCED_BREED_1_ID],
        ColumnDefs[Column.SaleLot.ADVANCED_BREED_1_HD],
        ColumnDefs[Column.SaleLot.ADVANCED_BREED_2_ID],
        ColumnDefs[Column.SaleLot.ADVANCED_BREED_2_HD],
        ColumnDefs[Column.SaleLot.ADVANCED_BREED_3_ID],
        ColumnDefs[Column.SaleLot.ADVANCED_BREED_3_HD],
      ],
    },
    ColumnDefs[Column.SaleLot.PUBLIC_DESCRIPTION],
    ColumnDefs[Column.SaleLot.PUBLIC_FAULT_DESCRIPTION],
    ColumnDefs[Column.SaleLot.PART_OF_RUN],
    ColumnDefs[Column.SaleLot.TAG_NUMBER],
  ].filter(Boolean) as (ColDef | ColGroupDef)[];
